import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { ArrowLeft, Save, Package } from 'lucide-react';

interface SupplyFormData {
  item_name: string;
  category: string;
  category_other: string;
  manufacturer: string;
  catalog_number: string;
  lot_number: string;
  expiration_date: string;
  quantity_on_hand: number;
  par_level_min: number;
  par_level_max: number;
  unit_cost: number;
  storage_location: string;
  notes: string;
}

export default function SupplyCreateRoute() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SupplyFormData>({
    item_name: '',
    category: 'Sutures',
    category_other: '',
    manufacturer: '',
    catalog_number: '',
    lot_number: '',
    expiration_date: '',
    quantity_on_hand: 0,
    par_level_min: 0,
    par_level_max: 0,
    unit_cost: 0,
    storage_location: '',
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: async (data: SupplyFormData) => {
      // Insert supply
      const { data: supply, error: supplyError } = await supabase
        .from('or_surgical_supplies')
        .insert({
          item_name: data.item_name,
          category: data.category,
          category_other: data.category === 'Other' ? data.category_other : null,
          manufacturer: data.manufacturer,
          catalog_number: data.catalog_number,
          lot_number: data.lot_number || null,
          expiration_date: data.expiration_date || null,
          quantity_on_hand: data.quantity_on_hand,
          par_level_min: data.par_level_min || null,
          par_level_max: data.par_level_max || null,
          unit_cost: data.unit_cost,
          storage_location: data.storage_location,
          notes: data.notes || null,
          is_active: true,
        })
        .select()
        .single();

      if (supplyError) throw supplyError;

      // Create initial "Received" transaction if quantity > 0
      if (data.quantity_on_hand > 0) {
        const { error: txnError } = await supabase
          .from('or_supply_transactions')
          .insert({
            supply_id: supply.supply_id,
            transaction_type: 'Received',
            quantity_change: data.quantity_on_hand,
            quantity_after: data.quantity_on_hand,
            lot_number: data.lot_number || null,
            reference_type: 'manual',
            notes: 'Initial stock receipt',
          });

        if (txnError) throw txnError;
      }

      return supply;
    },
    onSuccess: (supply) => {
      navigate(`/supplies/${supply.supply_id}`);
    },
    onError: (error: any) => {
      console.error('Error creating supply:', error);
      alert(`Error creating supply: ${error.message}`);
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.item_name.trim()) {
      newErrors.item_name = 'Item name is required';
    }
    if (!formData.manufacturer.trim()) {
      newErrors.manufacturer = 'Manufacturer is required';
    }
    if (!formData.catalog_number.trim()) {
      newErrors.catalog_number = 'Catalog number is required';
    }
    if (formData.category === 'Other' && !formData.category_other.trim()) {
      newErrors.category_other = 'Please specify category';
    }
    if (!formData.storage_location.trim()) {
      newErrors.storage_location = 'Storage location is required';
    }
    if (formData.unit_cost < 0) {
      newErrors.unit_cost = 'Unit cost cannot be negative';
    }
    if (formData.quantity_on_hand < 0) {
      newErrors.quantity_on_hand = 'Quantity cannot be negative';
    }
    if (formData.par_level_min && formData.par_level_max && formData.par_level_min > formData.par_level_max) {
      newErrors.par_level_min = 'Min cannot exceed max';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (field: keyof SupplyFormData, value: any) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <div className="min-h-full bg-pt-bg">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Link
            to="/supplies"
            className="text-pt-text-muted hover:text-pt-gold transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-pt-gold font-['Rajdhani'] tracking-wide">
              Add New Supply Item
            </h1>
            <p className="text-pt-text-muted text-sm mt-1">
              Create a new surgical supply record
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-pt-surface border border-pt-border rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Item Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-pt-text mb-2">
                Item Name <span className="text-pt-red">*</span>
              </label>
              <input
                type="text"
                value={formData.item_name}
                onChange={(e) => handleChange('item_name', e.target.value)}
                className={`w-full bg-pt-bg border ${errors.item_name ? 'border-pt-red' : 'border-pt-border'} rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold`}
                placeholder="e.g., Vicryl 3-0 Suture"
              />
              {errors.item_name && (
                <p className="text-pt-red text-xs mt-1">{errors.item_name}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Category <span className="text-pt-red">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold"
              >
                <option value="Sutures">Sutures</option>
                <option value="Dressings">Dressings</option>
                <option value="Implants">Implants</option>
                <option value="Disposables">Disposables</option>
                <option value="Instruments">Instruments</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Category Other (conditional) */}
            {formData.category === 'Other' && (
              <div>
                <label className="block text-sm font-medium text-pt-text mb-2">
                  Specify Category <span className="text-pt-red">*</span>
                </label>
                <input
                  type="text"
                  value={formData.category_other}
                  onChange={(e) => handleChange('category_other', e.target.value)}
                  className={`w-full bg-pt-bg border ${errors.category_other ? 'border-pt-red' : 'border-pt-border'} rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold`}
                  placeholder="e.g., Specialty Equipment"
                />
                {errors.category_other && (
                  <p className="text-pt-red text-xs mt-1">{errors.category_other}</p>
                )}
              </div>
            )}

            {/* Manufacturer */}
            <div className={formData.category === 'Other' ? 'md:col-span-2' : ''}>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Manufacturer <span className="text-pt-red">*</span>
              </label>
              <input
                type="text"
                value={formData.manufacturer}
                onChange={(e) => handleChange('manufacturer', e.target.value)}
                className={`w-full bg-pt-bg border ${errors.manufacturer ? 'border-pt-red' : 'border-pt-border'} rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold`}
                placeholder="e.g., Ethicon"
              />
              {errors.manufacturer && (
                <p className="text-pt-red text-xs mt-1">{errors.manufacturer}</p>
              )}
            </div>

            {/* Catalog Number */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Catalog Number <span className="text-pt-red">*</span>
              </label>
              <input
                type="text"
                value={formData.catalog_number}
                onChange={(e) => handleChange('catalog_number', e.target.value)}
                className={`w-full bg-pt-bg border ${errors.catalog_number ? 'border-pt-red' : 'border-pt-border'} rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold`}
                placeholder="e.g., J494G"
              />
              {errors.catalog_number && (
                <p className="text-pt-red text-xs mt-1">{errors.catalog_number}</p>
              )}
            </div>

            {/* Lot Number */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Lot Number
              </label>
              <input
                type="text"
                value={formData.lot_number}
                onChange={(e) => handleChange('lot_number', e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold font-mono"
                placeholder="e.g., LOT2024-001"
              />
            </div>

            {/* Expiration Date */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Expiration Date
              </label>
              <input
                type="date"
                value={formData.expiration_date}
                onChange={(e) => handleChange('expiration_date', e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold"
              />
            </div>

            {/* Quantity on Hand */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Initial Quantity <span className="text-pt-red">*</span>
              </label>
              <input
                type="number"
                value={formData.quantity_on_hand}
                onChange={(e) => handleChange('quantity_on_hand', parseInt(e.target.value) || 0)}
                className={`w-full bg-pt-bg border ${errors.quantity_on_hand ? 'border-pt-red' : 'border-pt-border'} rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold`}
                min="0"
              />
              {errors.quantity_on_hand && (
                <p className="text-pt-red text-xs mt-1">{errors.quantity_on_hand}</p>
              )}
            </div>

            {/* Unit Cost */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Unit Cost <span className="text-pt-red">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-pt-text-muted">$</span>
                <input
                  type="number"
                  value={formData.unit_cost}
                  onChange={(e) => handleChange('unit_cost', parseFloat(e.target.value) || 0)}
                  className={`w-full bg-pt-bg border ${errors.unit_cost ? 'border-pt-red' : 'border-pt-border'} rounded pl-7 pr-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold`}
                  step="0.01"
                  min="0"
                />
              </div>
              {errors.unit_cost && (
                <p className="text-pt-red text-xs mt-1">{errors.unit_cost}</p>
              )}
            </div>

            {/* Par Level Min */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Par Level Min
              </label>
              <input
                type="number"
                value={formData.par_level_min}
                onChange={(e) => handleChange('par_level_min', parseInt(e.target.value) || 0)}
                className={`w-full bg-pt-bg border ${errors.par_level_min ? 'border-pt-red' : 'border-pt-border'} rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold`}
                min="0"
              />
              {errors.par_level_min && (
                <p className="text-pt-red text-xs mt-1">{errors.par_level_min}</p>
              )}
            </div>

            {/* Par Level Max */}
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Par Level Max
              </label>
              <input
                type="number"
                value={formData.par_level_max}
                onChange={(e) => handleChange('par_level_max', parseInt(e.target.value) || 0)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold"
                min="0"
              />
            </div>

            {/* Storage Location */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-pt-text mb-2">
                Storage Location <span className="text-pt-red">*</span>
              </label>
              <input
                type="text"
                value={formData.storage_location}
                onChange={(e) => handleChange('storage_location', e.target.value)}
                className={`w-full bg-pt-bg border ${errors.storage_location ? 'border-pt-red' : 'border-pt-border'} rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold`}
                placeholder="e.g., OR Storage Room A, Shelf 3"
              />
              {errors.storage_location && (
                <p className="text-pt-red text-xs mt-1">{errors.storage_location}</p>
              )}
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-pt-text mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="w-full bg-pt-bg border border-pt-border rounded px-3 py-2 text-pt-text focus:outline-none focus:border-pt-gold"
                rows={3}
                placeholder="Additional notes or special handling instructions..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <Link
              to="/supplies"
              className="px-6 py-2 bg-pt-surface border border-pt-border rounded text-pt-text hover:border-pt-red transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex items-center gap-2 px-6 py-2 bg-pt-gold text-pt-bg rounded hover:bg-pt-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {createMutation.isPending ? 'Creating...' : 'Create Supply'}
            </button>
          </div>
        </form>

        {/* Summary Card */}
        <div className="mt-6 bg-pt-surface border border-pt-border rounded-lg p-4">
          <div className="flex items-center gap-2 text-pt-gold mb-3">
            <Package className="w-5 h-5" />
            <h3 className="font-semibold">Summary</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-pt-text-muted text-xs">Initial Quantity</div>
              <div className="text-pt-text font-medium">{formData.quantity_on_hand}</div>
            </div>
            <div>
              <div className="text-pt-text-muted text-xs">Unit Cost</div>
              <div className="text-pt-text font-medium">${formData.unit_cost.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-pt-text-muted text-xs">Total Value</div>
              <div className="text-pt-gold font-medium">
                ${(formData.quantity_on_hand * formData.unit_cost).toFixed(2)}
              </div>
            </div>
            <div>
              <div className="text-pt-text-muted text-xs">Par Range</div>
              <div className="text-pt-text font-medium">
                {formData.par_level_min} - {formData.par_level_max}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
